import type { Priorite } from '../partage/api/types';
import { IDEE, MOTS_ETAT, MOTS_PRIORITE, RIEN_DEDANS } from '../partage/vocabulaire';
import './LegendeCarte.css';

/**
 * « Lire la carte ». Depuis le chantier `tete-du-projet` (décision 7 d'Igor, 2026-10-20), elle
 * vit dans l'ampoule de l'Aide, quand on est sur une Carte (`client/src/aide/Aide.tsx`) ; le
 * « ? » à côté des angles est parti.
 *
 * Les quatre glyphes et l'étiquette « Idée », puis les gestes. Ils ne peuvent pas vivre dans un
 * seul `title=` : une carte dont on ne sait pas que le clic descend et que le double-clic ouvre
 * la fiche est une carte qu'on regarde sans jamais s'en servir. Elle dit la règle de
 * `DEUX_GESTES` (vocabulaire.ts) et du tuto, en gras sur les deux gestes : un clic entre
 * dedans, un double-clic ouvre sa fiche, partout, la bulle du centre comprise (retour 3 des
 * testeurs). Un mot changé ici se change aussi là-bas.
 *
 * Les mots des états viennent du vocabulaire, jamais d'ici : une légende qui dirait
 * « terminé » là où la fiche dit « acquis » apprendrait à mal lire la carte.
 *
 * `enAvant` : l'importance que la carte fait grossir, `null` sans filtre. Le filtre est caché
 * depuis le chantier `tete-du-projet` (décision 5) : sans lui, la légende ne parle plus de
 * boutons qu'on ne voit pas.
 */
export function LegendeCarte({ enAvant }: { enAvant: Priorite | null }) {
  return (
    <div className="tableau-legende">
      <div className="tableau-legende-etats">
        <span className="tableau-legende-item">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="7.2" stroke="var(--trait-marque)" strokeWidth="1.4" />
          </svg>
          {MOTS_ETAT.a_faire}
        </span>
        <span className="tableau-legende-item">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle
              cx="8"
              cy="8"
              r="7.2"
              stroke="var(--monde)"
              strokeWidth="1.4"
              strokeDasharray="2.4 2.8"
            />
          </svg>
          {MOTS_ETAT.en_attente_confirmation}
        </span>
        <span className="tableau-legende-item">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="8" fill="var(--accent)" />
            <path d="M4.3 8.2l2.6 2.6 5-5.4" stroke="var(--surface)" strokeWidth="1.5" fill="none" />
          </svg>
          {MOTS_ETAT.fait}
        </span>
        <span className="tableau-legende-item">
          <span className="tableau-legende-place" aria-hidden="true" />
          {RIEN_DEDANS}
        </span>
        {/* Une idée gardée sans la faire (ticket 18 de retours-testeurs) : le même pointillé
            que « Rien dedans » sur la bulle, et c'est cette étiquette qui les distingue. */}
        <span className="tableau-legende-item">
          <span className="etiquette-idee" aria-hidden="true">
            {IDEE}
          </span>
          {MOTS_ETAT.sans_objet}
        </span>
      </div>

      <p className="tableau-legende-gestes">
        <b>Clic</b> entrer dedans&nbsp;· <b>double-clic</b> sa fiche
      </p>
      {/* La carte qui déplie sur place (ticket 06 de retours-testeurs), et la profondeur qui
          ouvre tout un niveau (ticket 13). */}
      <p className="tableau-legende-gestes">
        <b>›</b> ou <b>molette vers l’avant</b> sur une étape : l’ouvrir sur place
      </p>
      <p className="tableau-legende-gestes">
        <b>Profondeur</b> : ouvrir tout un niveau d’un coup&nbsp;· <b>molette sur le fond</b> : zoomer
      </p>
      {/* Hiérarchiser n'a plus de bouton (chantier `tete-du-projet`, décision 6 d'Igor) : la
          touche reste, et c'est ici qu'elle se dit. */}
      <p className="tableau-legende-gestes">
        <b>Hiérarchiser</b> : touche <b>h</b>
      </p>
      {/* Le filtre (chantier `revue-design`, ticket 04) : ce qu'il fait, et ce que dit le
          nombre posé sur une étape, seulement quand une importance est en avant. */}
      {enAvant && (
        <p className="tableau-legende-gestes">
          <b>{MOTS_PRIORITE[enAvant]}</b> : aux profondeurs 2 et 3, ce qui en porte grossit, le reste
          rétrécit&nbsp;· le nombre sur une étape : combien elle en contient
        </p>
      )}
    </div>
  );
}
